// Library Methods
import { useTranslation } from "react-i18next";
import { cloneElement, useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import AddEditInterventionModal from "./AddEditInterventionModal/AddEditInterventionModal";
import {
  BoltIcon,
  CollapseLeftIcon,
  CollapseRightIcon,
  EyeIcon,
  InjectionIcon,
  PlusIcon,
  ScissorsIcon,
} from "src/assets/icons";

// Styles
import {
  getSurfaceTokens,
  ModalCustomConfirmGrey,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useInterventionsData } from "src/services/patients/hooks/timeline-interventions/useInterventionsData";
import { useAddIntervention } from "src/services/patients/hooks/timeline-interventions/useAddIntervention";
import { useDeleteIntervention } from "src/services/patients/hooks/timeline-interventions/useDeleteIntervention";
import { useUpdateIntervention } from "src/services/patients/hooks/timeline-interventions/useUpdateIntervention";

// Types
import {
  AddPatientInterventionType,
  PatientInterventionType,
  PatientType,
} from "src/services/patients/types";

// Utils
import { sortByDate } from "src/utils/date/dateHelper";

type TimelineSidebarType = {
  selectedPatient: PatientType;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

type ChartPoint = {
  x: number; // intervention type index
  y: number; // timestamp (ms) — larger = newer = higher on chart
  intervention: PatientInterventionType["interventions"][0];
  color: string;
  icon: JSX.Element;
};

const Y_AXIS_WIDTH = 45;
const CHART_RIGHT_MARGIN = 10;

const TimelineSidebar = ({
  selectedPatient,
  isCollapsed = false,
  onToggleCollapse,
}: TimelineSidebarType) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { surface, onSurface, mutedText, dividerBorder } =
    getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const {
    data: timelineData,
    isLoading: isLoadingTimeline,
    isFetching: isFetchingTimeline,
  } = useInterventionsData(clinicInfo?.id, selectedPatient?.id);
  const { mutate: addMutate } = useAddIntervention(
    clinicInfo?.id,
    selectedPatient?.id
  );
  const { mutate: editMutate } = useUpdateIntervention(
    clinicInfo?.id,
    selectedPatient?.id
  );
  const { mutate: deleteMutate } = useDeleteIntervention(
    clinicInfo?.id,
    selectedPatient?.id
  );

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [typeToEdit, setTypeToEdit] =
    useState<PatientInterventionType["interventions"][0]>();
  const [timelineEvents, setTimelineEvents] = useState<
    PatientInterventionType["interventions"]
  >([]);

  useEffect(() => {
    if (timelineData?.interventions && !isLoadingTimeline) {
      setTimelineEvents(timelineData?.interventions);
    }
  }, [timelineData?.interventions, isLoadingTimeline]);

  const interventionTypes = useMemo(
    () => [
      {
        id: 0,
        type: "Other",
        label: t("Other"),
        color: "#202338",
        icon: <EyeIcon width="20px" />,
      },
      {
        id: 1,
        type: "Injection",
        label: t("Injection"),
        color: "#202338",
        icon: <InjectionIcon width="20px" />,
      },
      {
        id: 2,
        type: "Laser",
        label: t("Laser"),
        color: "#202338",
        icon: <BoltIcon width="20px" />,
      },
      {
        id: 3,
        type: "Surgery",
        label: t("Surgery"),
        color: "#202338",
        icon: <ScissorsIcon width="20px" />,
      },
    ],
    [t]
  );

  const closeModal = useCallback(() => {
    setIsOpenModal(false);
    setTypeToEdit(undefined);
  }, []);

  const saveTimelineEvent = useCallback(
    (newEvent: AddPatientInterventionType) => {
      if (typeToEdit) {
        editMutate({
          interventionId: typeToEdit.id,
          interventionData: { ...typeToEdit, ...newEvent },
        });
      } else {
        addMutate({ ...newEvent });
      }
    },
    [typeToEdit, editMutate, addMutate]
  );

  const deleteTimelineEvent = useCallback(
    (idToDelete: string) => {
      deleteMutate(idToDelete);
    },
    [deleteMutate]
  );

  const todayMs = useMemo(() => DateTime.now().startOf("day").toMillis(), []);

  // x = intervention type index, y = timestamp (recharts puts larger y values at top)
  const chartData = useMemo<ChartPoint[]>(() => {
    const sorted = sortByDate(timelineEvents, "interventionDate");
    return sorted.map((event) => {
      const typeIndex = interventionTypes.findIndex(
        (tp) => tp.type === event.interventionType
      );
      const typeInfo =
        typeIndex >= 0 ? interventionTypes[typeIndex] : interventionTypes[0];
      return {
        x: typeIndex >= 0 ? typeIndex : 0,
        y: DateTime.fromISO(event.interventionDate).toMillis(),
        intervention: event,
        color: typeInfo.color,
        icon: typeInfo.icon,
      };
    });
  }, [timelineEvents, interventionTypes]);

  const oldestMs = useMemo(() => {
    const threeMonthsAgo = DateTime.now().minus({ months: 3 }).toMillis();
    if (!chartData.length) return threeMonthsAgo;
    return Math.min(Math.min(...chartData.map((d) => d.y)), threeMonthsAgo);
  }, [chartData]);

  const timeDomain = useMemo(() => {
    const pad = (todayMs - oldestMs) * 0.04;
    return [oldestMs - pad, todayMs + pad];
  }, [oldestMs, todayMs]);

  const rangeOverOneYear = useMemo(
    () => todayMs - oldestMs > 365 * 24 * 60 * 60 * 1000,
    [oldestMs, todayMs]
  );

  const axisTicks = useMemo(() => {
    const ticks: number[] = [];
    if (rangeOverOneYear) {
      let current = DateTime.fromMillis(oldestMs)
        .startOf("year")
        .plus({ years: 1 });
      while (current.toMillis() <= todayMs) {
        ticks.push(current.toMillis());
        current = current.plus({ years: 1 });
      }
    } else {
      let current = DateTime.fromMillis(oldestMs)
        .startOf("month")
        .plus({ months: 1 });
      while (current.toMillis() <= todayMs) {
        ticks.push(current.toMillis());
        current = current.plus({ months: 1 });
      }
    }
    return ticks;
  }, [oldestMs, todayMs, rangeOverOneYear]);

  const renderDot = (props: any) => {
    const { cx, cy, payload } = props as {
      cx: number;
      cy: number;
      payload: ChartPoint;
    };
    if (!cx || !cy || !payload) return <g />;
    const r = 14;
    return (
      <g
        style={{ cursor: "pointer" }}
        onClick={() => {
          if (isFetchingTimeline) return;
          setTypeToEdit(payload.intervention);
          setIsOpenModal(true);
        }}
      >
        <circle cx={cx} cy={cy} r={r} fill={payload.color} />
        <foreignObject
          x={cx - r}
          y={cy - r}
          width={r * 2}
          height={r * 2}
          style={{ pointerEvents: "none" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              color: "#FFF",
            }}
          >
            {cloneElement(payload.icon, { width: "14px", height: "14px" })}
          </div>
        </foreignObject>
      </g>
    );
  };

  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload as ChartPoint;
    const formattedDate = DateTime.fromMillis(point.y).toFormat("yyyy-MM-dd");
    const typeLabel =
      interventionTypes.find(
        (tp) => tp.type === point.intervention.interventionType
      )?.label ?? t("Other");
    return (
      <Box
        sx={{
          bgcolor: surface,
          border:
            theme.palette.mode === "dark"
              ? "1px solid #5B6D7A"
              : "1px solid #5B6D7A30",
          borderRadius: "4px",
          p: "8px",
          boxShadow: "none",
          maxWidth: "200px",
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "10px" }}>
          {typeLabel} | {formattedDate}
        </Typography>
        {point.intervention.notes && (
          <Typography
            sx={{ fontSize: "10px", mt: "4px", wordBreak: "break-word" }}
          >
            {point.intervention.notes}
          </Typography>
        )}
      </Box>
    );
  };

  if (isCollapsed) {
    return (
      <Box
        role="button"
        onClick={onToggleCollapse}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100%",
          width: "100%",
          p: "14px 8px",
          cursor: "pointer",
          backgroundColor: surface,
        }}
      >
        <Box
          sx={{
            color: mutedText,
          }}
        >
          <CollapseLeftIcon />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              whiteSpace: "nowrap",
              userSelect: "none",
              textTransform: "uppercase",
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {t("Intervention Timeline")}
            </span>{" "}
            ({timelineEvents?.length})
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Grid
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <Box
        role="button"
        onClick={onToggleCollapse}
        sx={{
          display: "flex",
          padding: "14px 8px",
          gap: "8px",
          alignItems: "center",
          justifyContent: "flex-start",
          borderBottom: dividerBorder,
          minWidth: 0,
          cursor: "pointer",
          backgroundColor: surface,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "4px",
            alignItems: "center",
            color: mutedText,
          }}
        >
          <CollapseRightIcon />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "4px",
            minWidth: 0,
            overflow: "hidden",
            mr: "8px",
          }}
        >
          <Typography
            color={onSurface}
            fontSize="12px"
            fontWeight={600}
            noWrap
            sx={{ minWidth: 0 }}
          >
            {t("Intervention Timeline")}
          </Typography>
          <Typography
            color={onSurface}
            fontSize="12px"
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            ({timelineEvents?.length})
          </Typography>
        </Box>
      </Box>

      <Box sx={{ pt: "8px", px: "8px" }}>
        <Button
          startIcon={<PlusIcon />}
          sx={{
            ...ModalCustomConfirmGrey(),
            width: "100%",
            ...(theme.palette.mode === "dark" && {
              backgroundColor: "#202338",
              border: "1px solid #202338",
              "&:hover": {
                backgroundColor: "#12141C",
                border: "1px solid #12141C",
              },
            }),
          }}
          onClick={() => {
            setTypeToEdit(undefined);
            setIsOpenModal(true);
          }}
        >
          {t("Add Event")}
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!timelineEvents.length ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: "16px",
              height: "100%",
            }}
          >
            <Typography fontSize="14px" textAlign="center">
              {isLoadingTimeline
                ? t("Loading...")
                : t("No timeline events found.")}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Column headers aligned with chart columns */}
            <Box
              sx={{
                display: "flex",
                pl: `${Y_AXIS_WIDTH}px`,
                pr: `${CHART_RIGHT_MARGIN}px`,
                pt: "8px",
                pb: "4px",
              }}
            >
              {interventionTypes.map((type) => (
                <Box
                  key={type.type}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "2px",
                  }}
                >
                  {cloneElement(type.icon, {
                    width: "14px",
                    height: "14px",
                    color: theme.palette.mode === "dark" ? "#FFF" : type.color,
                  })}
                  <Typography
                    sx={{
                      fontSize: "10px",
                      color: mutedText,
                      lineHeight: 1.2,
                      textAlign: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {type.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{
                    top: 8,
                    right: CHART_RIGHT_MARGIN,
                    bottom: 0,
                    left: 0,
                  }}
                >
                  {interventionTypes.map((_, i) => (
                    <ReferenceLine
                      key={`vline-${i}`}
                      x={i}
                      stroke={
                        theme.palette.mode === "dark"
                          ? "#FFFFFF20"
                          : "#5B6D7A20"
                      }
                      strokeWidth={1}
                    />
                  ))}
                  <XAxis
                    dataKey="x"
                    type="number"
                    domain={[-0.5, interventionTypes.length - 0.5]}
                    ticks={interventionTypes.map((_, i) => i)}
                    tick={false}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    dataKey="y"
                    type="number"
                    scale="time"
                    domain={timeDomain}
                    ticks={axisTicks}
                    tickFormatter={(value) =>
                      rangeOverOneYear
                        ? DateTime.fromMillis(value).toFormat("yyyy")
                        : DateTime.fromMillis(value)
                            .setLocale(i18n.language)
                            .toFormat("MMM")
                            .toUpperCase()
                    }
                    axisLine={{
                      stroke:
                        theme.palette.mode === "dark"
                          ? "#FFFFFF40"
                          : "#5B6D7A40",
                    }}
                    tickLine={false}
                    tick={{
                      fontSize: 11,
                      fill: mutedText,
                    }}
                    width={Y_AXIS_WIDTH}
                  />
                  <Tooltip
                    content={renderTooltip}
                    cursor={false}
                    wrapperStyle={{ transition: "none" }}
                  />
                  <Scatter
                    data={chartData}
                    shape={renderDot}
                    isAnimationActive={false}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </Box>

      <AddEditInterventionModal
        interventionTypes={interventionTypes}
        isOpen={isOpenModal}
        onClose={closeModal}
        typeToEdit={typeToEdit}
        onSave={saveTimelineEvent}
        onDelete={deleteTimelineEvent}
      />
    </Grid>
  );
};

export default TimelineSidebar;
